import {createSelector} from 'reselect';
import {sortBy} from 'lodash';
import SLICES from '../../selectors/slices';
import Utility from '../../util/utilityfunctions';
import {documentTypes, documentTypesShowedOnLeftNav} from '../../util/uiconstants';
import { getSortedSections } from '../../util/utils';

/* ****************************** aresTaskList slice ****************************** */

export const getTasksSlice = (state) => state.aresTaskList;

export const getTasksByIdSlice = createSelector([getTasksSlice], (tasksSlice) => tasksSlice?.byId);

export const getTasksAllIds = createSelector([getTasksSlice], (tasksSlice) => tasksSlice?.allIds);

export const getFilteredTaskIds = (state, filter) => {
	const taskList = getTasksSlice(state);
	const allIds = taskList?.allIds || [];
	let result = [];

	for (let i = 0; i < allIds.length; i++) {
		let task = taskList?.byId[allIds[i]];

		if (filter(task)) {
			result.push(allIds[i]);
		}
	}

	return result;
};

export const getTaskIdsWithFitDocuments = (state) => {
	return getFilteredTaskIds(state, (task) => task.fitDocuments?.length > 0);
};

export const getTaskDetails = (state, taskId) => {
	const _taskId = taskId?.toLowerCase();
	return _taskId ? state.aresTaskDetails[_taskId] : [];
};

export const getTaskById = (state, taskId, documentTypeId) => {
	const _taskId = taskId?.toLowerCase();

	const allowedDocumentTypes = [
		documentTypes.ACCOUNT_CONCLUSION,
		documentTypes.ACCOUNT_STANDARD_WORKING_PAPER_TEMPORARY
	];

	if (allowedDocumentTypes.indexOf(documentTypeId) !== -1) {
		return getTaskDetails(state, _taskId);
	}

	return _taskId ? getTasksSlice(state)?.byId[_taskId] : [];
};

export const getTaskActivityById = (state, taskId) => {
	const _taskId = taskId?.toLowerCase();
	return _taskId ? getTasksSlice(state)?.byId[_taskId] : [];
};

export const getTasksByTaskIdList = (state, taskIds, mapper) => {
	let _transformer = (state, taskId) => (mapper ? mapper(getTaskById(state, taskId)) : getTaskById(state, taskId));
	let tasks = taskIds?.map((taskId) => _transformer(state, taskId)).filter((task) => task !== undefined);

	return tasks;
};

export const getTasksList = (state) => {
	let tasksList = getTasksSlice(state);
	return tasksList;
};
/**
 * Gets a collection of taskIds stored in the aresTaskList normalized slice, sorted by the property name provided in the sortPropertyName parameter.
 * @param {Object} state Redux store.
 * @param {String} sortPropertyName Name of the property to sort the tasks by. May be a string or an array of property names (see lodash's sortBy function).
 * @param {Function} taskFilter Optional filter function to apply to the tasks before sorting.
 * @returns {Array} A collection of taskIds sorted by the property name provided in the sortPropertyName parameter.
 */
export const getSortedTaskIds = (state, sortPropertyName, taskFilter) => {
	const _taskFilter = taskFilter || ((task) => true);
	const tasksByIdSlice = getTasksByIdSlice(state);
	return sortBy(Object.values(tasksByIdSlice), sortPropertyName)
		.filter(_taskFilter)
		.map((task) => task.id);
};

/* **************************** aresTaskDocuments slice **************************** */

export const getTaskDocumentsSlice = (state) => state.aresTaskDocuments || {};
export const getTaskDocumentsByIdSlice = createSelector(
	[getTaskDocumentsSlice],
	(state) => state?.documents?.byId || {}
);

export const getTaskDocumentByTaskId = (state, taskId) => {
	const _taskId = taskId?.toLowerCase();
	return getTaskDocumentsSlice(state)[_taskId];
};

export const getTaskDocuments = (state) => {
	const taskDocumentsSlice = Object.values(getTaskDocumentsByIdSlice(state));
	if (taskDocumentsSlice && taskDocumentsSlice.length > 0) {
		return taskDocumentsSlice;
	}
	return [];
};

// TODO: Review implementation. Store may get re-structured to be indexed by docId instead of taskId
export const getTaskDocumentByDocumentId = (state, documentId) => {
	const taskDocumentsByIdSlice = getTaskDocumentsByIdSlice(state);
	let document = taskDocumentsByIdSlice[documentId];
	return document;
};

export const getDocumentTasks = (state, documentId) => {
	let doc = getTaskDocumentByDocumentId(state, documentId);
	if (doc) {
		return getTasksByTaskIdList(state, doc.tasks);
	}
	return [];
};

export const getTaskDocumentsByDocumentIdList = (state, documentIds) => {
	const taskDocumentsByIdSlice = getTaskDocumentsByIdSlice(state);
	let documents;

	if (Array.isArray(documentIds)) {
		documents = {};
		for (const docId of documentIds) {
			documents[docId] = taskDocumentsByIdSlice[docId];
		}
	}

	return documents;
};

export const getLinkedDocumentsByDocumentId = (state, documentId) => {
	const taskDocumentsList = Object.values(getTaskDocumentsSlice(state));
	let linkedDocumentIds = taskDocumentsList?.find((doc) => doc.id === documentId)?.linkedDocuments;
	if (linkedDocumentIds && state.aresLinkedDocuments) {
		let linkedDocuments = [];
		linkedDocumentIds.map((docId) => {
			if (state.aresLinkedDocuments[docId]) {
				linkedDocuments.push(state.aresLinkedDocuments[docId]);
			}
		});
		return linkedDocuments;
	}
};

// Gets the form's first section with isSummary = true
export const getSummarySectionByDocumentId = (state, documentId) => {
	const document = getTaskDocumentByDocumentId(state, documentId);

	if (document) {
		// This only applies for Guided Workflow v2 documents
		const isGuidedWorkFlowV2 = Utility.isDocumentTypeFitV2(document.documentTypeId);

		if (isGuidedWorkFlowV2) {
			const headers = document.headerIds;
			const sectionsByIdSlice = getSectionsSlice(state)?.byId || {};

			let summarySectionId = null;
			let summarySection = null;
			if (!headers || headers.length === 0) {
				return null;
			}
			for (const headerId of headers) {
				const header = getHeaderById(state, headerId);
				if (header) {
					summarySectionId = header.sections.find(
						(sectionId) => sectionsByIdSlice[sectionId]?.isSummary && sectionsByIdSlice[sectionId]?.isDisplayed
					);
					if (summarySectionId) {
						summarySection = sectionsByIdSlice[summarySectionId];
						break;
					}
				}
			}
			return summarySection;
		}
	}
};

export const getLastSectionByDocumentId = (state, documentId) => {
	let lastFormSection;
	const doc = getTaskDocumentByDocumentId(state, documentId);

	if (doc) {
		const formHeadersByIdSlice = getHeadersSlice(state)?.byId;
		const formSectionsByIdSlice = getSectionsSlice(state)?.byId;

		const formHeadersDetails = doc.headerIds
			?.map((headerId) => formHeadersByIdSlice[headerId])
			.filter((header) => !header.hideHeader);
		let lastFormHeader;
		if (formHeadersDetails?.length > 0) {
			lastFormHeader = formHeadersDetails[formHeadersDetails.length - 1];
		}

		const formSectionsDetails = lastFormHeader?.sections
			?.map((sectionId) => formSectionsByIdSlice[sectionId])
			.filter((section) => section.isDisplayed);
		if (formSectionsDetails?.length > 0) {
			lastFormSection = formSectionsDetails[formSectionsDetails.length - 1];
		}
	}

	return lastFormSection;
};

/* ************************** aresFormHeaders slice ************************** */

export const getHeadersSlice = (state) => state.aresFormHeaders?.headers;
export const getHeaderById = (state, headerId) => getHeadersSlice(state)?.byId[headerId];
export const getAllHeaderIds = createSelector([getHeadersSlice], (aresFormHeaders) => aresFormHeaders?.allIds || []);
export const getSectionsSlice = (state) => state.aresFormHeaders?.sections;

// Gets a form's displayable headers for a given a taskId
export const getAllHeaderIdsWithDisplayedSections = (state, documentId) => {
	const headersSlice = getHeadersSlice(state);

	const headers = getTaskDocumentByDocumentId(state, documentId)?.headerIds;
	if (headers?.length > 0) {
		return headers.filter((id) => !headersSlice.byId[id].hideheader);
	} else {
		return [];
	}
};

export const filterHeaderIdsByCustomFilter = (state, headerIds, filter) => {
	const headersSlice = getHeadersSlice(state);
	const sectionSlice = getSectionsSlice(state);

	let filteredHeaderIds = headerIds?.filter((headerId) => {
		const headerDetails = headersSlice.byId[headerId];
		if (headerDetails && headerDetails.sections) {
			return headerDetails.sections.some((sectionId) => {
				const sectionDetails = sectionSlice.byId[sectionId];

				return sectionDetails && sectionDetails.isDisplayed;
			});
		}
	});
	if (filter) {
		filteredHeaderIds = filteredHeaderIds?.filter((headerId) => {
			const headerDetails = headersSlice.byId[headerId];
			return headerDetails && filter(headerDetails);
		});
	}
	return filteredHeaderIds;
};

// Get a form's headerIds from a taskId
export const getFormHeaderIdsFromTaskId = (state, taskId) => {
	const taskRelatedFormIds = getTaskById(state, taskId)?.fitDocuments;
	let headerIds = [];

	if (taskRelatedFormIds?.length > 0) {
		const relatedForm = getTaskDocumentByDocumentId(state, taskRelatedFormIds[0]);

		if (relatedForm) {
			headerIds = relatedForm.headerIds;
		}
	}

	return headerIds;
};

export const getSectionById = (state, sectionId) => state.aresFormHeaders.sections?.byId[sectionId];
export const getAllSections = createSelector([getSectionsSlice], (sectionsSlice) => {
	return sectionsSlice.allIds.map((sectionId) => sectionsSlice.byId[sectionId]);
});
export const getSections = (state) => {
	return getAllSections(state)?.filter((section) => section.isDisplayed);
};
export const getSectionsByIds = (state, sectionIds) => {
	return getAllSections(state)?.filter((section) => sectionIds?.some((sectionId) => section.id === sectionId));
};
export const getDisplayedSectionsByIds = (state, sectionIds) => {
	return getAllSections(state)?.filter(
		(section) => section.isDisplayed && sectionIds?.some((sectionId) => section.id === sectionId)
	);
};

export const filterSectionIdsByCustomFilter = (state, sectionIds, customFilter, sortSections = false) => {
	let filteredSections;
	const sectionsSlice = getSectionsSlice(state);
	let sectionsDetail = sectionIds?.map((sectionId) => sectionsSlice.byId[sectionId]);

	if (customFilter) {
		filteredSections = sectionsDetail?.filter((section) => {
			return section && customFilter(section);
		});
	} else {
		return sectionIds;
	}
	if(sortSections) {
		filteredSections = getSortedSections(filteredSections);
	}
	return filteredSections?.map((section) => section.id);
};

export const filterSectionIdsByIsDisplayed = (state, sectionIds) => {
	const sectionFilter = (section) => section.isDisplayed;

	const displayedSectionIds = filterSectionIdsByCustomFilter(state, sectionIds, sectionFilter);
	return displayedSectionIds;
};

export const getFormSectionsByHeaderId = (state, headerId) => {
	const headerData = getHeaderById(state, headerId);
	const sectionsSlice = getSectionsSlice(state);
	let sections = [];

	if (headerData?.sections && headerData.sections.length > 0) {
		sections = headerData.sections.map((sectionId) => sectionsSlice?.byId[sectionId]);
	}

	return sections;
};

/* ****************************** aresSignOffs slice ****************************** */

export const getAresSignOffsSlice = (state) => state.signOffs;
export const getAllAresSignOffs = createSelector([getAresSignOffsSlice], (signOffsSlice) => {
	return signOffsSlice.allIds.map((signOffId) => signOffsSlice.byId[signOffId]);
});
export const getAresSignOffsByIds = (state, approvalIds) => {
	const signOffs = getAllAresSignOffs(state);

	return signOffs?.filter((signOff) => approvalIds?.some((approvalId) => approvalId === signOff.id));
};
export const getUserPreferences = (state) => state.userPreferences;

export const getAresSignOffsByIdSlice = (state, approvalsIds) => {
	let value = {};
	approvalsIds.forEach((id) => {
		value[id] = getAresSignOffsSlice(state).byId[id];
	});
	return value;
};

export const getStoryBook = (state, documentId) => {
	const relatedForm = getTaskDocumentByDocumentId(state, documentId);
	return relatedForm?.storyBook;
};

export const getEngagement = (state) => state.engagement;

export const getEngagementPaceTypeId = (state) => getEngagement(state).paceTypeId || 0;

export const getEngagementIsMest = (state) => getEngagement(state).isMultiEntityEngagement || false;

//#region Reusable selectors
export const getSlice = (state) => (slice) => state[slice];

export const getSliceById = (state, slice) => getSlice(state)(slice).byId || {};

export const getSliceAllIds = (state, slice) => getSlice(state)(slice).allIds || [];

export const getEntityById = (state, slice, entityId) => getSliceById(state, slice)[entityId];

export const getAccountById = (state, accountId) => getEntityById(state, SLICES.ACCOUNTS, accountId);

export const getEstimateById = (state, estimateId) => getEntityById(state, SLICES.ESTIMATES, estimateId);

export const getSTEntityById = (state, multiEntityId) => getEntityById(state, SLICES.STENTITIES, multiEntityId);

export const getAccountsByAccountTypeId = (state, accountTypeId) =>
	getSliceAllIds(state, SLICES.ACCOUNTS)
		.map((accountId) => getEntityById(state, SLICES.ACCOUNTS, accountId))
		.filter((account) => !accountTypeId || account.accountTypeId === accountTypeId);

export const getAccountsByAccountTypeIds = (state, accountTypeIds) =>
	getSliceAllIds(state, SLICES.ACCOUNTS)
		.map((accountId) => getEntityById(state, SLICES.ACCOUNTS, accountId))
		.filter((account) => !accountTypeIds?.length > 0 || accountTypeIds.indexOf(account.accountTypeId) !== -1);

export const getAccountIdsByAccountTypeId = (state, accountTypeId) =>
	getAccountsByAccountTypeId(state, accountTypeId).map((account) => account.id);

export const getAccountsAllIds = (state) => getSliceAllIds(state, SLICES.ACCOUNTS);

export const getAccountBySmartEvidenceSourceId = (state, smartEvidenceSourceId) => {
	if (smartEvidenceSourceId) {
		const accounts = getSliceById(state, SLICES.ACCOUNTS);
		if (accounts && Object.keys(accounts).length > 0) {
			return Object.values(accounts).find((account) => {
				return account.id === smartEvidenceSourceId;
			});
		}
	}
};

export const getAssertionById = (state, assertionId) => getEntityById(state, SLICES.ASSERTIONS, assertionId);

export const getAssertionsRelatedToAccount = (state, assertionIds) =>
	getSliceAllIds(state, SLICES.ASSERTIONS)
		.map((assertionId) => getEntityById(state, SLICES.ASSERTIONS, assertionId))
		.filter((assertion) => assertionIds?.includes(assertion.id));

export const getRiskById = (state, riskId) => getEntityById(state, SLICES.RISKS, riskId);

export const getAssertionsByAccountId = (state, accountId) => {
	getSliceAllIds(state, SLICES.ASSERTIONS)
		.map((assertionId) => getEntityById(state, SLICES.ASSERTIONS, assertionId))
		.filter((assertion) => assertion.accountID === accountId);
};

export const getOptionsForFormBody = (state, sectionId, bodyId) =>
	state.formBodyData.find((data) => data.sectionId === sectionId)?.bodies.find((data) => data.id === bodyId);

export const getJournalSourceAllIds = (state) => getSliceAllIds(state, SLICES.JOURNALSOURCE);

export const getJournalSourceById = (state) =>
	getSliceAllIds(state, SLICES.JOURNALSOURCE).map((id) => getEntityById(state, SLICES.JOURNALSOURCE, id));

export const documentMissesApprovals = (state, relatedDocuments) => {
	let missingApprovals = false;

	relatedDocuments &&
		relatedDocuments.length > 0 &&
		relatedDocuments.forEach((document) => {
			if (document.documentTypeId === documentTypes.PAPERPROFILE) {
				return;
			}

			let preparer, reviewer;
			let approvals = Utility.formatGetAllDocumentsApprovalsForSignOffsControl(
				document.approvals,
				state.userPreferences
			);
			preparer = approvals.find((doc) => {
				return doc.isPreparer === true;
			});
			reviewer = approvals.find((doc) => {
				return doc.isPreparer === false;
			});
			if (!preparer || !reviewer) {
				missingApprovals = true;
			}
		});

	return missingApprovals;
};

export const getStEntitiesSlice = (state) => state.stEntities;

export const getAllStEntities = (state, isManageEntities) => {
	const rootSlice = isManageEntities ? getStEntitiesSlice(state)?.manageEntities : getStEntitiesSlice(state);

	return rootSlice?.allIds.map((x) => rootSlice?.byId[x]);
};

export const getStEntitiesPagingOptions = (state) => {
	return getStEntitiesSlice(state)?.pagingOptions;
};

export const getStEntitiesGroupedByFunc = (state, prop, func) => {
	let slice = getAllStEntities(state).sort(func);
	return _.orderBy(slice, [prop]);
};

export const getValidationCountForDocumentIds = (state, documentIds) => {
	let validations = [];
	let acum = -1;
	documentIds.forEach((documentId) => {
		const document = getTaskDocumentsByIdSlice(state)[documentId];
		if (Object.keys(state.validations).includes(documentId) && document.approvals.length > 0) {
			acum = 0;
			validations = validations.concat(state.validations[documentId]);
		}
	});
	if (validations.length > 0) {
		acum = 0;
		validations.forEach((validation) => {
			acum += validation.validationCount;
		});
	}
	return acum;
};

const selectFormBodyData = (state) => state.formBodyData;

export const selectBodyGuidances = (sectionId, bodyId) => {
	return createSelector([selectFormBodyData], (formBodyData) => {
		if (formBodyData) {
			const section = formBodyData.find((data) => data.sectionId === Number(sectionId));

			if (section && Array.isArray(section.bodies)) {
				const formBody = section.bodies.find((body) => body.id === Number(bodyId));

				if (formBody && formBody.guidanceDocuments) {
					return formBody.guidanceDocuments;
				}
			}
		}

		return [];
	});
};

export const selectBodyDocuments = (bodyId) => {
	return createSelector([(store) => store.formBodyRelatedDocuments], (formBodyRelatedDocuments) => {
		if (formBodyRelatedDocuments[bodyId]) {
			return formBodyRelatedDocuments[bodyId].data;
		}

		return [];
	});
};

export const selectUploadDocumentIds = () => {
	return createSelector([(store) => store.bulkRelate], (bulkRelate) => {
		if (Array.isArray(bulkRelate.uploadDocumentIds)) {
			return bulkRelate.uploadDocumentIds;
		}

		return [];
	});
};

const selectKnowledgeForms = (store) => store.knowledgeForms;

export const selectKnowledgeForm = (knowledgeFormId) => {
	return createSelector([selectKnowledgeForms], (knowledgeForms) => {
		if (knowledgeForms.allIds.includes(Number(knowledgeFormId))) {
			return knowledgeForms.byId[Number(knowledgeFormId)];
		}

		return null;
	});
};

//V2 Left navigation buttons.
function getNextDestination(tasks, currentTask, currentTaskIndex, currentDocIndex, documents) {
	let next = {};

	if (currentTask?.fitDocuments) {
		//It's the last document, we go to the next task, first document.
		if (currentDocIndex === currentTask.fitDocuments.length - 1) {
			next.task = tasks[currentTaskIndex + 1];
			next.document = documents[next.task.fitDocuments[0]];
		} else {
			//We have 1 more document available to navigate to within the same task.
			next.task = currentTask;
			next.document = documents[currentTask.fitDocuments[currentDocIndex + 1]];
		}
	}

	return {
		next
	};
}

function getPreviousDestination(tasks, currentTask, currentTaskIndex, currentDocIndex, documents) {
	let previous = {};

	if (currentTask?.fitDocuments) {
		//It's the first document of the task. We have to go to the previous task, first document.
		if (currentDocIndex === 0) {
			previous.task = tasks[currentTaskIndex - 1];
			previous.document = documents[previous.task.fitDocuments[0]];
		} else {
			//We have 1 more document available to navigate to within the same task.
			previous.task = currentTask;
			previous.document = documents[currentTask.fitDocuments[currentDocIndex - 1]];
		}
	}

	return {
		previous
	};
}

export const getNavigationDestinations = (store, taskId, docId) => {
	const documents = getTaskDocumentsByIdSlice(store);

	let tasks = getTasksByTaskIdList(store, getV2NavRelevantTaskIds(store), (task) => {
		return {
			...task,
			fitDocuments: task.fitDocuments?.filter(
				(docId) => documentTypesShowedOnLeftNav.indexOf(documents[docId].documentTypeId) > -1
			)
		};
	});

	if (tasks.length <= 1 || Object.values(documents).length < 2) {
		return {};
	}

	let currentTaskIndex = tasks.findIndex((t) => t.id === taskId);

	let currentTask;
	let currentDocIndex;

	if (currentTaskIndex !== -1) {
		currentTask = tasks[currentTaskIndex];
		currentDocIndex = currentTask?.fitDocuments?.findIndex((id) => id === docId);
	}

	//It's the last activity, last document
	if (currentTask && currentDocIndex === currentTask.fitDocuments.length - 1 && currentTaskIndex === tasks.length - 1) {
		return {
			...getPreviousDestination(tasks, currentTask, currentTaskIndex, currentDocIndex, documents)
		};
	} else {
		//It's the first activity, first document
		if (currentDocIndex === 0 && currentTaskIndex === 0) {
			return {
				...getNextDestination(tasks, currentTask, currentTaskIndex, currentDocIndex, documents)
			};
		}
		//We are somewhat in the middle.
		else {
			return {
				...getPreviousDestination(tasks, currentTask, currentTaskIndex, currentDocIndex, documents),
				...getNextDestination(tasks, currentTask, currentTaskIndex, currentDocIndex, documents)
			};
		}
	}
};

export const getV2NavRelevantTaskIds = (state) => {
	const documents = getTaskDocumentsByIdSlice(state);

	return getFilteredTaskIds(
		state,
		(task) =>
			task.fitDocuments?.filter((docId) => documentTypesShowedOnLeftNav.indexOf(documents[docId].documentTypeId) > -1)
				.length > 0
	);
};
